import React, { useEffect } from "react"
import {
  arrayPush,
  change,
  Field,
  FieldArray,
  formValueSelector,
  getFormSyncErrors,
  setSubmitFailed,
} from "redux-form"
import { FORM_NAMES } from "constants/index"
import { Card, CardBody, CardHeader, Col, Row } from "reactstrap"
import { SaveButton } from "components/Buttons"
import {
  TextField,
  RadioField,
  CheckboxField,
  SingleSelectField,
} from "components/RenderInput"
import { connect } from "react-redux"
import {
  renderQuestionsForm,
  maxPointsCalculator,
  minPointsCalculator,
} from "./utils"
import {
  addQuestionnaire,
  displayLockWarningModal,
  updateQuestionnaire,
} from "store/actions"
import { mapQuestionnaireToApi, mapUpdatedQuestionnaireToApi } from "../utils"
import PropTypes from "prop-types"
import QuestionnairePreview from "./questionnairePreview"

const Questionnaire = props => {
  useEffect(() => {
    if (props.questionnaire) {
      props.change(
        "questionnaire_form.create.retakeText",
        props.questionnaire.retake_button_text
      )
      props.change(
        "questionnaire_form.create.retakeOption",
        props.questionnaire.retake_action.toString()
      )
      props.change(
        "questionnaire_form.create.questions",
        props.questionnaire.questions.map(question => ({
          questionType: question.type.toString(),
          answers: question.answers.map(answer => ({
            ...answer,
            value: answer.value,
          })),
          id: question.id,
          question: question.question,
          order: question.order,
        }))
      )
      props.change(
        "questionnaire_form.create.headline",
        props.questionnaire?.headline || ""
      )
      props.change(
        "questionnaire_form.create.subtext",
        props.questionnaire?.subtext || ""
      )
      props.change(
        "questionnaire_form.create.allowRetake",
        props.questionnaire.retake_period !== null
      )
      props.change(
        "questionnaire_form.create.retakeValue",
        props.questionnaire?.retake_period !== null
          ? props.questionnaire?.retake_period.toString()
          : null
      )
      props.change(
        "questionnaire_form.create.retakeType",
        props.questionnaire.retake_period_type !== null
          ? props.questionnaire?.retake_period_type.toString()
          : null
      )
      props.change("questionnaire_form.create.name", props.questionnaire?.name)
    }
  }, [props.questionnaire])

  const submitHandler = () => {
    if (
      (props.formSyncErrors.hasOwnProperty("questionnaire_form") &&
        ((props.formSyncErrors?.questionnaire_form.hasOwnProperty("create") &&
          Object.keys(props.formSyncErrors?.questionnaire_form?.create)
            .length === 1 &&
          props.formSyncErrors?.questionnaire_form?.create.hasOwnProperty(
            "name"
          )) ||
          !props.formSyncErrors?.questionnaire_form.hasOwnProperty(
            "create"
          ))) ||
      !props.formSyncErrors.hasOwnProperty("questionnaire_form")
    ) {
      if (!props.feature.entity_id) {
        const [questions, questionnaire] = mapQuestionnaireToApi({
          ...props.createFormValue,
          eventId: props?.selectedEvent?.id,
          menu_feature: true,
          menu_item_id: props.feature.id,
          name: props.feature.label,
          order: 0,
          status: 0,
          userId: props.userId,
          vendorId: props.vendorId,
        })
        props.addQuestionnaire({ questions, questionnaire })
      } else {
        const [newQuestions, updatedQuestions, questionnaire] =
          mapUpdatedQuestionnaireToApi(
            props.createFormValue,
            props.questionnaire
          )
        props.updateQuestionnaire({
          newQuestions,
          updatedQuestions,
          questionnaire,
        })
      }
    } else {
      props.setSubmitFailed(["questionnaire_form.create.questions"])
    }
  }

  const handleFieldFocus = event => {
    if (props?.hasReplies) {
      event.preventDefault()
      props.displayLockWarningModal()
    }
  }

  return (
    <>
      <Card>
        <CardBody>
          <Row className="mb-3">
            <Col>
              <h3>{props.feature.label}</h3>
            </Col>
            {props.isListItem && (
              <Col xs="auto">
                <SaveButton
                  type="button"
                  content="Cancel"
                  color="light"
                  onClick={props.backButtonHandler}
                />
              </Col>
            )}
            <Col xs="auto">
              <SaveButton
                content="Save"
                type="button"
                onClick={
                  props.isListItem ? props.handleAddListItem : submitHandler
                }
              />
            </Col>
          </Row>
          <Row>
            <Col>
              {props.isListItem && (
                <Card className="my-1">
                  <CardHeader>
                    <h4>CREATE A NAME FOR YOUR QUESTIONNAIRE</h4>
                  </CardHeader>
                  <CardBody>
                    <Row className="mb-2">
                      <Col>
                        <Field name="name" component={TextField} label="Name" />
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              )}
              <Card>
                <CardHeader>
                  <h4>QUESTIONNAIRE HEADLINE/SUBTEXT</h4>
                </CardHeader>
                <CardBody>
                  <Row className="mb-3">
                    <Col>
                      <Field
                        name="headline"
                        component={TextField}
                        label="Headline"
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <Field
                        name="subtext"
                        component={TextField}
                        label="Subtext"
                      />
                    </Col>
                  </Row>
                </CardBody>
              </Card>
              <Card>
                <CardHeader>
                  <h4>QUESTIONNAIRE RETAKE</h4>
                </CardHeader>
                <CardBody>
                  <Row className="mt-3">
                    <Col>
                      <Field
                        name="retakeOption"
                        value="1"
                        type="radio"
                        label="Restrict to taking once"
                        component={RadioField}
                        onChange={(event, newValue, previousValue, name) => {
                          if (previousValue === "2") {
                            props.change(
                              "questionnaire_form.create.allowRetake",
                              false
                            )
                            props.change(
                              "questionnaire_form.create.retakeValue",
                              null
                            )
                            props.change(
                              "questionnaire_form.create.retakeType",
                              null
                            )
                          }
                        }}
                      />
                    </Col>
                  </Row>
                  <Row className="my-1">
                    <Col>
                      <Field
                        name="retakeOption"
                        value="2"
                        type="radio"
                        label="Allow multiple retakes"
                        component={RadioField}
                      />
                    </Col>
                  </Row>
                  <Row className="my-1 ms-2 d-flex align-items-center">
                    <Col xs="auto">
                      <Field
                        name="allowRetake"
                        label="Allow retake after"
                        component={CheckboxField}
                        disabled={props.createFormValue?.retakeOption !== "2"}
                      />
                    </Col>
                    <Col xs={3} className="p-0">
                      <Field
                        name="retakeValue"
                        component={TextField}
                        type="number"
                        disabled={!props.createFormValue?.allowRetake}
                        min="0"
                      />
                    </Col>
                    <Col xs={4} className="p-1">
                      <Field
                        name="retakeType"
                        component={SingleSelectField}
                        options={[
                          {
                            label: "minutes",
                            value: "1",
                          },
                          {
                            label: "hours",
                            value: "2",
                          },
                          {
                            label: "days",
                            value: "3",
                          },
                        ]}
                        isSearchable={false}
                        disabled={!props.createFormValue?.allowRetake}
                      />
                    </Col>
                  </Row>
                  <Row className="my-1">
                    <Col xs={12}>
                      <Field
                        name="retakeOption"
                        value="3"
                        type="radio"
                        label="Start new each time"
                        component={RadioField}
                        onChange={(event, newValue, previousValue, name) => {
                          if (previousValue === "2") {
                            props.change(
                              "questionnaire_form.create.allowRetake",
                              false
                            )
                            props.change(
                              "questionnaire_form.create.retakeValue",
                              null
                            )
                            props.change(
                              "questionnaire_form.create.retakeType",
                              null
                            )
                          }
                        }}
                      />
                    </Col>
                  </Row>
                  <Row className="my-3">
                    <Col>
                      <Field
                        name="retakeText"
                        component={TextField}
                        label="Retake Button Text"
                      />
                    </Col>
                  </Row>
                </CardBody>
              </Card>
              <Card>
                <CardHeader>
                  <h4>QUESTIONNAIRE QUESTIONS</h4>
                </CardHeader>
                <CardBody>
                  <Row className="mb-3">
                    <Col xs={12}>
                      Add questions with answer options. Each answer will have a
                      point value. Total point tally will decide result to the
                      user.
                    </Col>
                  </Row>
                  <Row className="my-3">
                    <Col xs={12}>
                      <FieldArray
                        name="questions"
                        component={renderQuestionsForm}
                        rerenderOnEveryChange={true}
                        handleFieldFocus={handleFieldFocus}
                        hasReplies={props?.hasReplies}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <h5
                        style={{ cursor: "pointer" }}
                        onClick={
                          props?.hasReplies
                            ? () => {}
                            : () => {
                                props.addQuestionForm(
                                  "questionnaire_form.create.questions",
                                  {
                                    id: null,
                                    questionType: "1",
                                    answers: [
                                      { answer: "", value: 0 },
                                      { answer: "", value: 0 },
                                    ],
                                  }
                                )
                              }
                        }
                      >
                        + Add Question
                      </h5>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
              <Card>
                <CardHeader>
                  <h4>POINT CALCULATOR</h4>
                </CardHeader>
                <CardBody>
                  <Row>
                    <Col>
                      Minimum points currently:{" "}
                      {minPointsCalculator(props.createFormValue)} points
                    </Col>
                  </Row>
                  <Row className="my-1">
                    <Col>
                      Maximum points currently:{" "}
                      {maxPointsCalculator(props.createFormValue)} points
                    </Col>
                  </Row>
                </CardBody>
              </Card>
              <Row className="my-2">
                <Col className="d-flex justify-content-center">
                  <SaveButton
                    content="Save"
                    type="button"
                    onClick={
                      props.isListItem ? props.handleAddListItem : submitHandler
                    }
                  />
                </Col>
              </Row>
            </Col>
            <Col xl={4} className="mt-4 custom-features-preview">
              <QuestionnairePreview
                menu_bg_color={props.design?.menu_bg_color}
                menu_font_color={props.design?.menu_font_color}
                label={props.feature?.label}
                headline={props.formValues?.create?.headline}
                subtext={props.formValues?.create?.subtext}
                bgImage={
                  props.formValues?.createDesign?.backgroundImage?.editedImage
                }
                image={props.formValues?.createDesign?.image?.editedImage}
                bgColor={props.formValues?.createDesign?.backgroundColor}
                textColor={props.formValues?.createDesign?.textColor}
                buttonBgColor={props.formValues?.createDesign?.buttonBgColor}
                buttonTextColor={
                  props.formValues?.createDesign?.buttonTextColor
                }
                submitText={props.formValues?.createDesign?.submitText}
                questions={props.formValues?.create?.questions}
              />
            </Col>
          </Row>
        </CardBody>
      </Card>
    </>
  )
}

const selector = formValueSelector(FORM_NAMES.QUESTIONNAIRE)

const mapStateToProps = state => ({
  createFormValue: selector(state, "questionnaire_form.create"),
  formSyncErrors: getFormSyncErrors(FORM_NAMES.QUESTIONNAIRE)(state),
  selectedEvent: state.experiences.selectedEvent,
  vendorId: state.Login.vendorId,
  userId: state.Login.authUser.id,
  questionnaire: state.Questionnaire.questionnaire,
  formValues: selector(state, "questionnaire_form"),
})

const mapDispatchToProps = dispatch => ({
  setSubmitFailed: fields =>
    dispatch(setSubmitFailed(FORM_NAMES.QUESTIONNAIRE, ...fields)),
  change: (field, value) =>
    dispatch(change(FORM_NAMES.QUESTIONNAIRE, `${field}`, value)),
  addQuestionForm: (field, values) =>
    dispatch(arrayPush(FORM_NAMES.QUESTIONNAIRE, field, values)),
  addQuestionnaire: values => dispatch(addQuestionnaire(values)),
  updateQuestionnaire: values => dispatch(updateQuestionnaire(values)),
  displayLockWarningModal: () => dispatch(displayLockWarningModal()),
})

Questionnaire.propTypes = {
  feature: PropTypes.object,
  handleAddListItem: PropTypes.func,
  isListItem: PropTypes.bool,
  backButtonHandler: PropTypes.func,
}

export default connect(mapStateToProps, mapDispatchToProps)(Questionnaire)
